import { useCallback, useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { DEFAULT_MQTT_SETTINGS, type GenericResponse, type MqttSettings } from "@/lib/types";

export function MqttPage() {
  const [cfg, setCfg] = useState<MqttSettings>(DEFAULT_MQTT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setCfg(await api.get<MqttSettings>("/api/settings/mqtt"));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const resp = await api.post<GenericResponse>("/api/settings/mqtt", cfg);
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("MQTT-Direktempfang")}
        description={t("Geräte publizieren ohne externen Broker direkt an Leif (MQTT 3.1.1, QoS 0/1). Login: Benutzername = Client-ID, Passwort = API-Key aus den TCP-Clients. Änderungen greifen sofort, ohne Neustart.")}
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <form onSubmit={onSave} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex cursor-pointer items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                className="h-4 w-4 cursor-pointer accent-primary"
              />
              <span className="text-sm">{t("MQTT-Direktempfang aktiv")}</span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Port
              </span>
              <input
                type="number"
                min={1}
                max={65535}
                value={cfg.port}
                onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })}
                className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t("Container-intern. Nach außen mappt docker-compose (Standard: 41883 → 1883).")}
              </span>
            </label>

            <div className="flex flex-col gap-3">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={cfg.allow_anonymous}
                  onChange={(e) => setCfg({ ...cfg, allow_anonymous: e.target.checked })}
                  className="h-4 w-4 cursor-pointer accent-primary"
                />
                <span className="text-sm">{t("Geräte ohne Login zulassen")}</span>
              </label>
              {cfg.allow_anonymous ? (
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t("Client-ID für anonyme Geräte")}
                  </span>
                  <input
                    type="text"
                    value={cfg.anonymous_client_id}
                    onChange={(e) => setCfg({ ...cfg, anonymous_client_id: e.target.value })}
                    placeholder="mqtt"
                    autoComplete="off"
                    className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-[11px] text-muted-foreground">
                    {t("Nur für Sensoren, die keine Zugangsdaten setzen können. Den Port dann ausschließlich im vertrauenswürdigen Netz freigeben.")}
                  </span>
                </label>
              ) : null}
            </div>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
              </button>
            </div>
          </form>
        )}
      </Card>

      <Card
        title={t("Offline-Erkennung per Last Will")}
        description={t("Hinterlegt ein Gerät beim Verbinden ein Last Will, veröffentlicht Leif es als Log-Event, sobald die Verbindung abreißt (Standard-Level ERROR). Damit feuern Alarme und der Hallenplan zeigt Rot, ohne dass das Gerät selbst etwas melden muss.")}
      >
        <p className="text-[11px] text-muted-foreground">
          {t("Kein Broker-Ersatz: Subscriptions lehnt Leif ab, QoS 2 wird nicht unterstützt. Wer Fan-out an weitere Konsumenten braucht, betreibt einen eigenen Broker.")}
        </p>
      </Card>
    </div>
  );
}
